import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';
import { filterMrpWarehouseList } from '../utils/mrpWarehouseFilter';

/* ----------------------- Constants ----------------------- */
const VIEW_TYPES = ['Daily', 'Weekly', 'Monthly'];
const FORECAST_METHODS = ['Simple Average', 'Weighted Average'];
const HISTORY_SOURCES = ['Sales Orders', 'Deliveries and A/R Invoices'];
const INP = 'w-full border border-sap-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-sap-blue';

/** Max rows rendered in forecast item picker (search narrows the list) */
const FORECAST_ITEM_DROPDOWN_LIMIT = 3000;

/* helpers */
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN') : '';
const toISO = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';
const monthShort = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { month: 'short' });
};

function computePeriods(startDate, endDate) {
  const periods = [];
  if (!startDate || !endDate) return periods;
  const sd = new Date(startDate + 'T00:00:00');
  const ed = new Date(endDate + 'T00:00:00');
  const cursor = new Date(sd.getFullYear(), sd.getMonth(), 1);
  while (cursor <= ed) {
    const yy = cursor.getFullYear();
    const mm = String(cursor.getMonth() + 1).padStart(2, '0');
    periods.push(`${yy}-${mm}-01`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
}

function nextCode(existing) {
  const nums = existing
    .map(h => h.forecast_code)
    .filter(c => /^FC\d+$/.test(c))
    .map(c => parseInt(c.slice(2)));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return 'FC' + String(next).padStart(5, '0');
}

/** Coerce MySQL/json ids (number | string) for comparisons */
function numId(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* -----------------------------------------------------------
   Main Component
   ----------------------------------------------------------- */
export default function Forecasts() {
  const [mode, setMode] = useState('list');
  const [headers, setHeaders] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/mrp/forecast-headers');
      setHeaders(data || []);
    } catch { setError('Failed to load forecasts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };

  const openDetail = (code) => { setSelectedCode(code); setMode('detail'); setError(''); };
  const openNew = () => { setSelectedCode(null); setMode('detail'); setError(''); };
  const backToList = () => { setMode('list'); load(); };

  if (mode === 'detail') {
    return <ForecastDetail code={selectedCode} headers={headers} onBack={backToList} flash={flash} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">Forecasts</h1>
        <button type="button" onClick={openNew} className="btn-primary">
          + New Forecast
        </button>
      </div>

      {success && <Banner type="success">{success}</Banner>}
      {error && <Banner type="error">{error}</Banner>}

      {loading ? (
        <div className="py-16 text-center text-xs text-sap-textLight">Loading forecasts</div>
      ) : headers.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center text-xs text-sap-textLight">
          No forecast documents yet. Click "+ New Forecast" to create one.
        </div>
      ) : (
        <div className="data-table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                {['Forecast Code', 'Forecast Name', 'Start Date', 'End Date', 'View', 'Lines', 'Status', 'Created'].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {headers.map((h, i) => (
                <tr key={h.forecast_code}
                  onClick={() => openDetail(h.forecast_code)}
                  className={`cursor-pointer border-l-2 border-l-sap-blue/30 hover:bg-sap-blueLight/40 transition-colors
                    ${i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'}
                    ${i < headers.length - 1 ? 'border-b border-sap-border/60' : ''}`}>
                  <td className="px-3 py-2 font-bold text-sap-blue">{h.forecast_code}</td>
                  <td className="px-3 py-2">{h.forecast_name}</td>
                  <td className="px-3 py-2 text-sap-textLight">{fmtDate(h.start_date)}</td>
                  <td className="px-3 py-2 text-sap-textLight">{fmtDate(h.end_date)}</td>
                  <td className="px-3 py-2">{h.view_type}</td>
                  <td className="px-3 py-2 text-center font-semibold">{h.line_count}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-bold
                      ${h.status === 'Active' ? 'bg-green-50 text-green-700'
                        : h.status === 'Draft' ? 'bg-amber-50 text-amber-700'
                        : 'bg-gray-100 text-gray-500'}`}>
                      {h.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sap-textLight">{fmtDate(h.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -----------------------------------------------------------
   Forecast DETAIL � SAP B1 style
   ----------------------------------------------------------- */
function ForecastDetail({ code, headers, onBack, flash }) {
  const isNew = !code;

  const [form, setForm] = useState({
    forecast_code: '', forecast_name: '',
    start_date: '', end_date: '',
    view_type: 'Monthly', status: 'Active',
  });
  const periods = computePeriods(form.start_date, form.end_date);
  /** Share of table width for each month column (table-fixed; must sum with other col % to ~100) */
  const forecastPeriodColPct = periods.length > 0 ? 61 / periods.length : 0;

  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  /** Searchable item picker: which row is open + query string */
  const [itemPick, setItemPick] = useState({ row: null, q: '' });
  /** Fixed layer position for item dropdown (escapes overflow-x-auto / overflow-hidden parents) */
  const itemPickAnchorRef = useRef(null);
  const [itemPickLayer, setItemPickLayer] = useState(null);

  const itemPickFiltered = useMemo(() => {
    if (itemPick.row == null) return [];
    const q = itemPick.q.trim().toLowerCase();
    const base = !q
      ? items
      : items.filter(
          (it) =>
            (it.item_no || '').toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q)
        );
    return base.slice(0, FORECAST_ITEM_DROPDOWN_LIMIT);
  }, [itemPick.row, itemPick.q, items]);

  const syncItemPickLayer = useCallback(() => {
    if (itemPick.row == null) {
      setItemPickLayer(null);
      return;
    }
    const el = itemPickAnchorRef.current;
    if (!el || typeof el.getBoundingClientRect !== 'function') return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const maxH = Math.min(
      Math.floor(window.innerHeight * 0.65),
      Math.max(160, window.innerHeight - r.bottom - gap - 16)
    );
    setItemPickLayer({
      top: r.bottom + gap,
      left: r.left,
      width: Math.max(320, r.width),
      maxHeight: maxH,
    });
  }, [itemPick.row]);

  useLayoutEffect(() => {
    syncItemPickLayer();
  }, [syncItemPickLayer, itemPick.q, itemPick.row, periods.length, lines.length]);

  useEffect(() => {
    if (itemPick.row == null) return;
    const onWin = () => syncItemPickLayer();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [itemPick.row, syncItemPickLayer]);

  useEffect(() => {
    (async () => {
      try {
        const [itemData, whData] = await Promise.all([
          api.get('/mrp/items-list'),
          api.get('/mrp/warehouses'),
        ]);
        setItems(itemData || []);
        setWarehouses(filterMrpWarehouseList(whData || []));

        if (!isNew) {
          setLoading(true);
          const data = await api.get(`/mrp/forecast-headers/${encodeURIComponent(code)}`);
          setForm({
            forecast_code: data.forecast_code,
            forecast_name: data.forecast_name,
            start_date: toISO(data.start_date),
            end_date: toISO(data.end_date),
            view_type: data.view_type,
            status: data.status,
          });
          const grouped = {};
          (data.lines || []).forEach(l => {
            const key = `${l.item_id}_${l.warehouse_id || ''}`;
            if (!grouped[key]) {
              grouped[key] = {
                item_id: l.item_id, item_no: l.item_no, item_desc: l.item_desc,
                warehouse_id: l.warehouse_id, wh_code: l.wh_code || '',
                periods: {},
              };
            }
            grouped[key].periods[toISO(l.period_date)] = Number(l.quantity);
          });
          setLines(Object.values(grouped));
        } else {
          setForm(f => ({ ...f, forecast_code: nextCode(headers) }));
        }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [code, isNew, headers]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const addLine = () => {
    setLines(prev => [...prev, { item_id: '', item_no: '', item_desc: '', warehouse_id: '', wh_code: '', periods: {} }]);
    setDirty(true);
  };

  const pickForecastLineItem = (idx, item) => {
    const defWhId = numId(item.default_warehouse_id);
    let wh =
      defWhId != null ? warehouses.find((w) => numId(w.warehouse_id) === defWhId) : null;
    if (!wh && warehouses.length > 0) {
      wh = warehouses[0];
    }
    const wid = wh ? numId(wh.warehouse_id) : null;
    setItemPickLayer(null);
    setLines((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        item_id: numId(item.item_id),
        item_no: item.item_no || '',
        item_desc: item.description || '',
        warehouse_id: wid,
        wh_code: wh?.wh_code || '',
      };
      return next;
    });
    setItemPick({ row: null, q: '' });
    setDirty(true);
  };

  const clearForecastLineItem = (idx) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        item_id: '', item_no: '', item_desc: '',
        warehouse_id: null, wh_code: '',
        periods: next[idx].periods || {},
      };
      return next;
    });
    setItemPick({ row: idx, q: '' });
    setDirty(true);
  };

  const updateLineWarehouse = (idx, whId) => {
    const id = whId === '' || whId == null ? null : numId(whId);
    const wh = id != null ? warehouses.find(w => numId(w.warehouse_id) === id) : null;
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], warehouse_id: id, wh_code: wh?.wh_code || '' };
      return next;
    });
    setDirty(true);
  };

  const updateLineQty = (idx, period, val) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], periods: { ...next[idx].periods, [period]: val } };
      return next;
    });
    setDirty(true);
  };

  const removeLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form.forecast_code.trim()) return setError('Forecast Code is required');
    if (!form.forecast_name.trim()) return setError('Forecast Name is required');
    if (!form.start_date || !form.end_date) return setError('Start Date and End Date are required');
    setSaving(true); setError('');
    try {
      const flatLines = [];
      lines.forEach(l => {
        if (!l.item_id) return;
        periods.forEach(pd => {
          const qty = parseFloat(l.periods[pd]) || 0;
          flatLines.push({ item_id: l.item_id, warehouse_id: l.warehouse_id || null, period_date: pd, quantity: qty });
        });
      });
      if (isNew) {
        await api.post('/mrp/forecast-headers', form);
        if (flatLines.length > 0) {
          await api.put(`/mrp/forecast-headers/${encodeURIComponent(form.forecast_code)}`, { lines: flatLines });
        }
        flash('Forecast created successfully');
      } else {
        await api.put(`/mrp/forecast-headers/${encodeURIComponent(code)}`, { ...form, lines: flatLines });
        flash('Forecast updated successfully');
      }
      setDirty(false);
      onBack();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete forecast "${form.forecast_code}"?`)) return;
    try {
      await api.del(`/mrp/forecast-headers/${encodeURIComponent(code)}`);
      flash('Forecast deleted');
      onBack();
    } catch (e) { setError(e.message); }
  };

  const handleGenerated = async () => {
    setShowGenerate(false);
    try {
      const data = await api.get(`/mrp/forecast-headers/${encodeURIComponent(form.forecast_code)}`);
      setForm({
        forecast_code: data.forecast_code, forecast_name: data.forecast_name,
        start_date: toISO(data.start_date), end_date: toISO(data.end_date),
        view_type: data.view_type, status: data.status,
      });
      const grouped = {};
      (data.lines || []).forEach(l => {
        const key = `${l.item_id}_${l.warehouse_id || ''}`;
        if (!grouped[key]) {
          grouped[key] = {
            item_id: l.item_id, item_no: l.item_no, item_desc: l.item_desc,
            warehouse_id: l.warehouse_id, wh_code: l.wh_code || '', periods: {},
          };
        }
        grouped[key].periods[toISO(l.period_date)] = Number(l.quantity);
      });
      setLines(Object.values(grouped));
      setDirty(false);
      flash('Forecast generated from sales history');
    } catch (e) { setError(e.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sap-blue border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-xs text-sap-textLight">Loading forecast</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Title bar */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3.5 flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="text-xs text-sap-blue hover:underline font-semibold">Forecasts</button>
        <span className="text-sap-border">|</span>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sap-blue to-sap-headerTo flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
            </svg>
          </div>
          <div>
            <p className="text-2xs text-sap-textLight uppercase tracking-wider font-semibold">Forecast</p>
            <p className="text-sm font-bold text-sap-text">{form.forecast_name || 'New Forecast'}</p>
          </div>
        </div>
        <span className="ml-2 px-2 py-0.5 text-2xs font-bold rounded-lg bg-sap-blue/10 text-sap-blue">{form.forecast_code}</span>
        {dirty && <span className="text-2xs text-amber-600 font-semibold">Unsaved</span>}
        <div className="flex-1" />
        <button onClick={() => setShowGenerate(true)}
          className="px-3 py-1.5 text-xs border border-sap-blue text-sap-blue rounded-lg hover:bg-sap-blue/5 font-semibold transition-colors">
          Generate Forecast
        </button>
        {!isNew && (
          <button onClick={handleDelete}
            className="px-3 py-1.5 text-xs border border-red-300 text-red-500 rounded-lg hover:bg-red-50 font-semibold transition-colors">
            Delete
          </button>
        )}
        <button onClick={onBack}
          className="px-3 py-1.5 text-xs border border-sap-border rounded-lg text-sap-textLight hover:bg-gray-50 font-semibold">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn-primary disabled:opacity-60">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {error && <Banner type="error">{error}</Banner>}

      {/* Header fields */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Field label="Forecast Code">
            <input className={INP} value={form.forecast_code} onChange={e => set('forecast_code', e.target.value)} disabled={!isNew} />
          </Field>
          <Field label="Forecast Name">
            <input className={INP} value={form.forecast_name} onChange={e => set('forecast_name', e.target.value)} />
          </Field>
          <Field label="Start Date">
            <input type="date" className={INP} value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </Field>
          <Field label="End Date">
            <input type="date" className={INP} value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </Field>
          <Field label="View">
            <select className={INP} value={form.view_type} onChange={e => set('view_type', e.target.value)}>
              {VIEW_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={INP} value={form.status} onChange={e => set('status', e.target.value)}>
              {['Draft', 'Active', 'Closed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {/* Forecast Grid */}
      <div className="card">
        <div className="flex items-center justify-between px-4 py-2 border-b border-sap-border/40">
          <span className="text-xs font-semibold text-sap-text">Forecast Lines ({lines.length} items  {periods.length} periods)</span>
          <button onClick={addLine}
            className="px-2.5 py-1 text-2xs bg-sap-blue/10 text-sap-blue rounded-lg hover:bg-sap-blue/20 font-bold transition-colors">
            + Add Row
          </button>
        </div>

        {periods.length === 0 ? (
          <div className="py-8 text-center text-xs text-sap-textLight">
            Set Start Date and End Date above to define forecast periods.
          </div>
        ) : (
          <div className="w-full max-w-full overflow-hidden">
            <table className="w-full table-fixed text-xs border-separate border-spacing-0">
              <colgroup>
                <col style={{ width: '2%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '6%' }} />
                {periods.map((pd) => (
                  <col key={pd} style={{ width: `${forecastPeriodColPct}%` }} />
                ))}
                <col style={{ width: '5%' }} />
                <col style={{ width: '2%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-sap-border">
                  <th className="text-center px-0.5 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase tracking-wide">#</th>
                  <th className="text-left px-1 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase">Item No.</th>
                  <th className="text-left px-1 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase">Description</th>
                  <th className="text-left px-0.5 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase">Wh</th>
                  {periods.map((pd) => (
                    <th key={pd} className="text-center px-0.5 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase">
                      <span className="block">{monthShort(pd)}</span>
                      <span className="text-white/60 font-normal normal-case">{pd.slice(0, 4)}</span>
                    </th>
                  ))}
                  <th className="text-right px-0.5 py-1.5 font-semibold text-sap-textMuted text-[10px] leading-tight uppercase">Total</th>
                  <th className="p-0 w-6" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={6 + periods.length} className="py-8 text-center text-sap-textLight">
                    No lines yet. Click "+ Add Row" or use "Generate Forecast" to populate.
                  </td></tr>
                ) : lines.map((line, idx) => {
                  const rowTotal = periods.reduce((s, pd) => s + (parseFloat(line.periods[pd]) || 0), 0);
                  return (
                    <tr key={`${idx}-${line.item_id || 'new'}`} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'} border-b border-sap-border/40`}>
                      <td className="px-0.5 py-0.5 text-center text-sap-textLight align-middle text-[10px]">{idx + 1}</td>
                      <td className="px-0.5 py-0.5 align-middle">
                        <div className="relative w-full min-w-0">
                          {line.item_id ? (
                            <div className="flex items-center gap-0.5 h-6 px-0.5 border border-sap-border/60 rounded bg-white min-w-0">
                              <span className="font-semibold text-sap-blue text-[10px] truncate min-w-0 flex-1" title={line.item_no}>{line.item_no}</span>
                              <button
                                type="button"
                                title="Change item"
                                onClick={() => clearForecastLineItem(idx)}
                                className="shrink-0 text-red-400 hover:text-red-600 text-[10px] px-0.5 leading-none">
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="text"
                                autoComplete="off"
                                placeholder="Search…"
                                ref={itemPick.row === idx ? itemPickAnchorRef : undefined}
                                className="w-full min-w-0 h-6 px-1 text-[10px] border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue"
                                value={itemPick.row === idx ? itemPick.q : ''}
                                onChange={(e) => { setItemPick({ row: idx, q: e.target.value }); }}
                                onFocus={() => setItemPick({ row: idx, q: itemPick.row === idx ? itemPick.q : '' })}
                                onBlur={() => { setTimeout(() => setItemPick((p) => (p.row === idx ? { row: null, q: '' } : p)), 200); }}
                              />
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-0.5 py-0.5 text-sap-text align-middle min-w-0">
                        <span className="block truncate text-[10px] leading-snug" title={line.item_desc}>{line.item_desc}</span>
                      </td>
                      <td className="px-0.5 py-0.5 align-middle min-w-0">
                        <select className="w-full min-w-0 max-w-full h-6 px-0.5 text-[10px] border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue"
                          value={line.warehouse_id != null && line.warehouse_id !== '' ? String(line.warehouse_id) : ''} onChange={e => updateLineWarehouse(idx, e.target.value)}>
                            <option value="">—</option>
                          {warehouses.map(w => <option key={w.warehouse_id} value={String(w.warehouse_id)}>{w.wh_code}</option>)}
                        </select>
                      </td>
                      {periods.map((pd) => (
                        <td key={pd} className="px-0.5 py-0.5 align-middle min-w-0">
                          <input type="number" min="0" step="1"
                            className="w-full min-w-0 h-6 px-0.5 text-[10px] text-right border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue"
                            value={line.periods[pd] ?? ''}
                            onChange={e => updateLineQty(idx, pd, e.target.value)} />
                        </td>
                      ))}
                      <td className="px-0.5 py-0.5 text-right font-bold text-sap-text align-middle text-[10px] whitespace-nowrap tabular-nums">
                        {rowTotal > 0 ? Number(rowTotal).toLocaleString('en-IN') : ''}
                      </td>
                      <td className="px-0.5 py-0.5 text-center align-middle">
                        <button type="button" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-600 text-[10px] leading-none" title="Remove">✕</button>
                      </td>
                    </tr>
                  );
                })}
                {lines.length > 0 && (
                  <tr className="bg-sap-blue/5 font-bold border-t-2 border-sap-blue/30">
                    <td colSpan={4} className="px-2 py-1.5 text-sap-text text-[10px]">Column Total</td>
                    {periods.map((pd) => {
                      const colTotal = lines.reduce((s, l) => s + (parseFloat(l.periods[pd]) || 0), 0);
                      return <td key={pd} className="px-0.5 py-1.5 text-center text-sap-blue text-[10px] tabular-nums">{colTotal > 0 ? Number(colTotal).toLocaleString('en-IN') : ''}</td>;
                    })}
                    <td className="px-0.5 py-1.5 text-right text-sap-blue text-[10px] tabular-nums">
                      {(() => {
                        const gt = lines.reduce((s, l) => s + periods.reduce((ps, pd) => ps + (parseFloat(l.periods[pd]) || 0), 0), 0);
                        return gt > 0 ? Number(gt).toLocaleString('en-IN') : '';
                      })()}
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showGenerate && (
        <GenerateDialog form={form} items={items}
          onClose={() => setShowGenerate(false)} onGenerated={handleGenerated} setError={setError} />
      )}

      {itemPick.row != null && itemPickLayer && typeof document !== 'undefined' &&
        createPortal(
          <div
            role="listbox"
            className="bg-white border border-sap-border rounded-lg shadow-xl overflow-y-auto overflow-x-hidden"
            style={{
              position: 'fixed',
              top: itemPickLayer.top,
              left: itemPickLayer.left,
              width: itemPickLayer.width,
              maxHeight: itemPickLayer.maxHeight,
              zIndex: 10050,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {itemPickFiltered.length === 0 ? (
              <div className="px-2 py-2 text-2xs text-sap-textLight">No matching items</div>
            ) : (
              <>
                {itemPickFiltered.map((it) => (
                  <button
                    key={it.item_id}
                    type="button"
                    role="option"
                    onClick={() => pickForecastLineItem(itemPick.row, it)}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-sap-blue/10 border-b border-sap-border/30 last:border-0"
                  >
                    <span className="font-semibold text-sap-blue">{it.item_no}</span>
                    <span className="ml-1.5 text-sap-textLight block truncate">{it.description}</span>
                  </button>
                ))}
                {items.length > FORECAST_ITEM_DROPDOWN_LIMIT && itemPickFiltered.length >= FORECAST_ITEM_DROPDOWN_LIMIT && (
                  <div className="px-2 py-1.5 text-2xs text-sap-textLight bg-[#F8F9FA] border-t border-sap-border/40">
                    Showing first {FORECAST_ITEM_DROPDOWN_LIMIT} matches — type to narrow the list.
                  </div>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}


/* -----------------------------------------------------------
   Generate Forecasts - Setup  (SAP B1 style dialog)
   ----------------------------------------------------------- */
function GenerateDialog({ form, items, onClose, onGenerated, setError }) {
  const [selectBy, setSelectBy]       = useState('Item');
  const [itemFrom, setItemFrom]       = useState('');
  const [itemTo, setItemTo]           = useState('');
  const [itemGroup, setItemGroup]     = useState('All');
  const [forWarehouses, setForWarehouses] = useState(false);
  const [showAdvanced, setShowAdvanced]   = useState(false);
  const [baseSalesHistory, setBaseSalesHistory] = useState(true);
  const [method, setMethod]           = useState('Simple Average');
  const [historySource, setHistorySource] = useState('Sales Orders');
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [generating, setGenerating]   = useState(false);
  const [genError, setGenError]       = useState('');

  const itemNos = items.map(i => i.item_no).sort();

  const handleGenerate = async () => {
    if (!form.forecast_code || !form.start_date || !form.end_date) {
      setGenError('Save the forecast header first (code, dates required)');
      return;
    }
    setGenerating(true); setGenError('');
    try {
      await api.post('/mrp/forecast-headers/generate', {
        forecast_code: form.forecast_code,
        forecast_name: form.forecast_name,
        start_date: form.start_date,
        end_date: form.end_date,
        view_type: form.view_type,
        select_by: selectBy,
        item_no_from: itemFrom,
        item_no_to: itemTo,
        item_group: itemGroup,
        generate_for_warehouses: forWarehouses,
        base_on_sales_history: baseSalesHistory,
        forecast_method: method,
        history_source: historySource,
        history_start_date: historyStartDate || null,
      });
      onGenerated();
    } catch (e) {
      setGenError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        {/* Title bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-sap-border/60 rounded-t-2xl bg-gray-50 border-b border-sap-border">
          <h2 className="text-sm font-bold text-white">Generate Forecasts  Setup</h2>
          <button onClick={onClose} className="text-sap-textMuted hover:text-sap-text text-lg leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {genError && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{genError}</div>}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Select Items By">
              <select className={INP} value={selectBy} onChange={e => setSelectBy(e.target.value)}>
                <option value="Item">Item</option>
                <option value="Item Group">Item Group</option>
              </select>
            </Field>
          </div>

          {selectBy === 'Item' && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Item No. From">
                <input list="itemNoList" className={INP} placeholder="Enter item no." value={itemFrom} onChange={e => setItemFrom(e.target.value)} />
                <datalist id="itemNoList">
                  {itemNos.map(n => <option key={n} value={n} />)}
                </datalist>
              </Field>
              <Field label="To">
                <input list="itemNoList2" className={INP} placeholder="Enter item no." value={itemTo} onChange={e => setItemTo(e.target.value)} />
                <datalist id="itemNoList2">
                  {itemNos.map(n => <option key={n} value={n} />)}
                </datalist>
              </Field>
            </div>
          )}

          {selectBy === 'Item Group' && (
            <Field label="Item Group">
              <select className={INP} value={itemGroup} onChange={e => setItemGroup(e.target.value)}>
                <option value="All">All</option>
                {[...new Set(items.filter(i => i.item_group_id).map(i => i.item_group_id))].sort().map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </Field>
          )}

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={forWarehouses} onChange={e => setForWarehouses(e.target.checked)}
              className="w-4 h-4 rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
            <span className="text-xs font-semibold text-sap-text">Generate Forecast for Warehouses</span>
          </label>

          {/* Advanced Settings */}
          <div className="border border-sap-border/60 rounded-xl overflow-hidden">
            <button onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-sap-text hover:bg-sap-blue/5 transition-colors text-left">
              <span className={`transform transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>?</span>
              Advanced Settings
            </button>

            {showAdvanced && (
              <div className="px-4 pb-4 pt-1 border-t border-sap-border/40 space-y-3">
                <p className="text-2xs font-bold text-sap-textLight uppercase tracking-wider">System-Generated Forecast</p>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={baseSalesHistory} onChange={e => setBaseSalesHistory(e.target.checked)}
                    className="w-4 h-4 rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
                  <span className="text-xs text-sap-text">Base on Sales History</span>
                  <select className={`ml-auto ${INP} w-auto min-w-[160px]`} value={method}
                    onChange={e => setMethod(e.target.value)} disabled={!baseSalesHistory}>
                    {FORECAST_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>

                {baseSalesHistory && (
                  <div className="pl-6 space-y-2">
                    {HISTORY_SOURCES.map(s => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="radio" name="histSrc" value={s} checked={historySource === s}
                          onChange={e => setHistorySource(e.target.value)}
                          className="w-3.5 h-3.5 text-sap-blue focus:ring-sap-blue/30" />
                        <span className="text-xs text-sap-text">{s}</span>
                      </label>
                    ))}
                    <Field label="History Start Date">
                      <input type="date" className={INP} value={historyStartDate}
                        onChange={e => setHistoryStartDate(e.target.value)} />
                    </Field>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-sap-border/40 bg-[#F8F9FA] rounded-b-2xl">
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-textLight hover:bg-gray-50 font-semibold">
            Cancel
          </button>
          <button onClick={handleGenerate} disabled={generating}
            className="btn-primary disabled:opacity-60">
            {generating ? 'Generating…' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* -----------------------------------------------------------
   Shared helpers
   ----------------------------------------------------------- */
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-2xs font-semibold text-sap-textLight uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function Banner({ type, children }) {
  const cls = type === 'error'
    ? 'bg-red-50 border-red-200 text-red-600'
    : 'bg-green-50 border-green-200 text-green-700';
  return <div className={`px-3 py-2 rounded-lg border text-xs ${cls}`}>{children}</div>;
}